// Ad fields (M14): the ad's content (dish name, description, price …) and how a bound text object shows it.
// Pure; unit-tested. M20 (owner decision): ads are SPANISH ONLY. The data keeps its { es, en } shape so saved
// ads still open, but the form edits only Spanish and the English slot mirrors it (see `esOnly`).
import type { AdData, AdLang, BrandData, CurrencyFormat, TextNode } from '../model/types';

export const DEFAULT_CURRENCY: CurrencyFormat = { symbol: 'Q', decimals: 2, space: true, after: false };

/** "Q 45.00" by default (owner decision, PROGRESS.md V2). Empty when there is no price. */
export function formatPrice(v: number | null | undefined, c: CurrencyFormat = DEFAULT_CURRENCY): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '';
  const n = v.toFixed(c.decimals).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const sym = c.symbol.trim();
  if (!sym) return n;
  return c.after ? `${n}${c.space ? ' ' : ''}${sym}` : `${sym}${c.space ? ' ' : ''}${n}`;
}

/** Reads a price typed by a person: "45", "45.5", "Q45", "45,00" (comma as decimal mark), "1,250.00". */
export function parsePrice(s: string): number | null {
  let t = s.replace(/[^\d.,-]/g, '');
  if (!t) return null;
  // A single comma followed by 1–2 digits at the end is a decimal mark; other commas group thousands.
  if (/^[^.]*,\d{1,2}$/.test(t)) t = t.replace(',', '.');
  else t = t.replace(/,/g, '');
  const v = Number(t);
  return Number.isFinite(v) ? Math.round(v * 100) / 100 : null;
}

/** One ad text, Spanish only (the English slot mirrors it). */
export const esOnly = (es: string) => ({ es, en: es });

export const SAMPLE_AD: AdData = {
  dishName: esOnly('Bowl de pollo a la parrilla'),
  description: esOnly('Arroz, aguacate y pico de gallo'),
  price: 45,
  oldPrice: 59,
  headline: esOnly('Pruébelo hoy'),
  tagline: esOnly('Pida ya'),
  items: [
    { name: esOnly('Hamburguesa clásica'), price: 39 },
    { name: esOnly('Tacos de pollo (3)'), price: 35 },
    { name: esOnly('Ensalada César'), price: 32 },
    { name: esOnly('Pasta alfredo'), price: 48 },
    { name: esOnly('Canasta de papas'), price: 18 },
    { name: esOnly('Limonada natural'), price: 15 },
    { name: esOnly('Pastel de chocolate'), price: 25 },
    { name: esOnly('Café'), price: 12 },
  ],
};

/** The text a bound text object shows, from the ad and the brand. Unbound → its own text. */
export function boundText(n: TextNode, ad: AdData | undefined, brand: BrandData | undefined): string {
  const b = n.bind;
  if (!b || b.role === 'free') return n.text;
  const lang: AdLang = b.lang ?? 'es';
  const cur = brand?.currency ?? DEFAULT_CURRENCY;
  const a = ad ?? SAMPLE_AD;
  switch (b.role) {
    case 'dishName':
      return a.dishName[lang];
    case 'description':
      return a.description[lang];
    case 'headline':
      return a.headline[lang];
    case 'tagline':
      return a.tagline[lang];
    case 'price':
      return formatPrice(a.price, cur);
    case 'oldPrice':
      return formatPrice(a.oldPrice, cur);
    case 'restaurant':
      return brand?.name ?? '';
    case 'contact':
      return brand?.contact ?? '';
    case 'itemName':
      return a.items[b.item ?? 0]?.name[lang] ?? '';
    case 'itemPrice':
      return formatPrice(a.items[b.item ?? 0]?.price ?? null, cur);
  }
  return n.text;
}

/** Which fields a template's text objects use (the form shows only these). */
export function usedRoles(texts: TextNode[]): {
  roles: Set<string>;
  items: number;
} {
  const roles = new Set<string>();
  let items = 0;
  for (const t of texts) {
    if (!t.bind || t.bind.role === 'free') continue;
    roles.add(t.bind.role);
    if (t.bind.role === 'itemName' || t.bind.role === 'itemPrice')
      items = Math.max(items, (t.bind.item ?? 0) + 1);
  }
  return { roles, items };
}
